/**
 * Storyworld - Main Application
 * 
 * Hex-based MMORPG exploration game
 */

import React, { useState, useEffect, useCallback, useReducer, useRef } from 'react';
import { Routes, Route, Navigate, useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { getFirestore } from 'firebase/firestore';
import './App.css';
import { MapContainer } from './components/MapContainer';
import { ActionUI } from './components/ActionUI';
import { CombatUI } from './components/CombatUI';
import { AuthScreen } from './components/AuthScreen';
import { EmailVerificationScreen } from './components/EmailVerificationScreen';
import { WorldSelectionScreen } from './components/WorldSelectionScreen';
import { LandingPage } from './components/LandingPage';
import { StoryContainer } from './components/StoryContainer';
import { SettingsPanel } from './components/SettingsPanel';
import { BurgerMenu } from './components/BurgerMenu';
import { TravelModal } from './components/TravelModal';
import { LoreCodex } from './components/LoreCodex';
import { OnboardingModal } from './components/OnboardingModal';
import { TerrainMap, TerrainType, AxialCoord, TERRAIN_NAMES } from './types/terrain';
import { Monster, CombatState, Player } from './types/player';
import { generateMap, getTerrainDistribution, selectRandomSpawnHex } from './utils/mapGenerator';
import { usePlayer } from './contexts/PlayerContext';
import { useAuth } from './contexts/AuthContext';
import { useAudioPlayer } from './contexts/AudioPlayerContext';
import { calculateFitToScreenZoom } from './utils/mapUtils';
import { hexNeighbors, hexDistance } from './utils/hexMath';
import { initializeMonster, PartySize } from './utils/combat';
import { useExploration } from './hooks/useExploration';
import { getRandomMonster } from './data/bestiary';
import { getTravelTimeSetting, getForagingTimeSetting } from './components/SettingsPanel';
import { findShortestPath, calculateStaminaCost, calculateTravelTime } from './utils/hexPathfinding';
import { ensurePlayerReady, getOutOfStaminaMessage } from './utils/dailyReset';
import { createClientStateSnapshot } from './utils/stateSync';
import { useClickValidation } from './hooks/useClickValidation';
import { FEATURES } from './config/features';
import { db } from './firebase';
import { doc, getDoc, updateDoc, deleteField } from 'firebase/firestore';

// Foraging yields by terrain type
const FORAGING_YIELDS: Record<TerrainType, number> = {
  [TerrainType.Desert]: 0,
  [TerrainType.Settlement]: 0,
  [TerrainType.Mountain]: 1,
  [TerrainType.Tundra]: 2, // Grassland/Tundra
  [TerrainType.Hills]: 2,
  [TerrainType.Forest]: 3,
  [TerrainType.River]: 3,
  [TerrainType.Ocean]: 0,
};

// Protected Game Route Wrapper (defined outside App to prevent recreation)
interface GameRouteWrapperProps {
  renderGame: () => React.ReactElement;
  user: any;
  isGenerating: boolean;
  map: TerrainMap | null;
  selectedWorld: string | null;
  setSelectedWorld: (worldId: string | null) => void;
  handleLeaveGame: () => void;
}

const GameRouteWrapper: React.FC<GameRouteWrapperProps> = ({
  renderGame,
  user,
  isGenerating,
  map,
  selectedWorld,
  setSelectedWorld,
  handleLeaveGame,
}) => {
  const { worldId } = useParams<{ worldId: string }>();

  // Set selected world from URL parameter
  React.useEffect(() => {
    if (worldId && worldId !== selectedWorld) {
      setSelectedWorld(worldId);
    }
  }, [worldId, selectedWorld, setSelectedWorld]);

  // Check authentication
  if (!user) {
    return <Navigate to={`/login?redirectTo=/play/${worldId}`} replace />;
  }

  // Check email verification
  if (!user.emailVerified) {
    return <Navigate to="/verify" replace />;
  }

  // Loading screen during map generation
  if (isGenerating || !map) {
    return (
      <div className="App">
        <div className="loading-screen">
          <h1>🗺️ Generating World...</h1>
          <div className="loading-spinner"></div>
          <p>Creating a 200×200 hex map (~40,000 hexes)</p>
          <p className="loading-details">
            This may take a few seconds...
          </p>
          <button 
            className="logout-button"
            onClick={handleLeaveGame}
            data-no-sync="true"
          >
            🚪 Leave Game
          </button>
        </div>
      </div>
    );
  }

  // Render the main game
  return renderGame();
};

function App() {
  // Use AuthContext for authentication
  const { user, loading: authLoading, signOut } = useAuth();
  
  // Router navigation
  const navigate = useNavigate();
  
  // Use PlayerContext for centralized state management
  const { player, loading: playerLoading, position: playerPosition, movePlayer, updatePosition, markHexesExplored, restoreStamina, updatePlayer, exploredHexes, travelState, completeTravel, cancelTravel, advanceTravelToHex, stopTravelAtHex, startTravel, isTraveling, foragingState, startForaging, completeForaging, isForaging, getForagingTimeRemaining, consumeRations, addRations, restingState, startResting, completeResting, isResting, getRestingTimeRemaining, heal, combatState, setCombatState, pendingEncounter, setPendingEncounter, activeQuestId, questState, setActiveQuestId, setQuestState, questPlaythrough, questDataLoaded, updateQuestPlaythrough } = usePlayer();
  
  // Exploration hook for story generation
  const { explorationState, handleHexEntry, clearStory } = useExploration();
  
  // Shared audio player (persists across view changes)
  const audioPlayer = useAudioPlayer();
  
  // DEBUG: Add function to check Firestore pendingEncounter
  useEffect(() => {
    if (user) {
      (window as any).checkFirestorePendingEncounter = async () => {
        const db = getFirestore();
        const playerDoc = await getDoc(doc(db, 'players', user.uid));
        const data = playerDoc.data();
        console.log('🔍 FIRESTORE CHECK - pendingEncounter:', data?.pendingEncounter);
        console.log('🔍 FIRESTORE CHECK - combatState:', data?.combatState);
        console.log('🔍 FIRESTORE CHECK - position:', data?.position);
        console.log('🔍 FIRESTORE CHECK - full player data:', data);
      };
      console.log('✅ Debug function added: Call checkFirestorePendingEncounter() in console');
    }
  }, [user]);
  
  // Stop audio narration when player moves to a new hex
  useEffect(() => {
    if (playerPosition && audioPlayer) {
      audioPlayer.pause();
      console.log('🔇 Audio stopped due to hex change:', playerPosition);
    }
  }, [playerPosition?.q, playerPosition?.r]);
  
  const [selectedWorld, setSelectedWorld] = useState<string | null>(null);
  const gameContainerRef = useRef<HTMLDivElement>(null);
  const hasShownOnboardingRef = useRef(false); // Prevent showing modal multiple times per session
  const [map, setMap] = useState<TerrainMap | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [viewMode, setViewMode] = useState<'map' | 'action'>('action'); // Start on action screen
  const [selectedHex, setSelectedHex] = useState<AxialCoord | null>(null);
  const [selectedTerrain, setSelectedTerrain] = useState<TerrainType | null>(null);
  const [currentZoom, setCurrentZoom] = useState<number>(1);
  const [showSettings, setShowSettings] = useState(false);
  const [showLoreCodex, setShowLoreCodex] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [actionCompletionMessage, setActionCompletionMessage] = useState<string | null>(null);
  const [isRespawning, setIsRespawning] = useState(false);
  
  // Travel modal state
  const [showTravelModal, setShowTravelModal] = useState(false);
  const [proposedRoute, setProposedRoute] = useState<AxialCoord[] | null>(null);
  const [modalDestination, setModalDestination] = useState<AxialCoord | null>(null);
  
  // Animation state management with useReducer for atomic updates
  const [animationState, dispatchAnimation] = useReducer(
    (state: {
      travelTimeComplete: boolean;
      pendingDestination: AxialCoord | null;
    }, action: 
      | { type: 'START_ANIMATION'; destination: AxialCoord }
      | { type: 'COMPLETE_ANIMATION' }
      | { type: 'RESET' }
    ) => {
      switch (action.type) {
        case 'START_ANIMATION':
          return { travelTimeComplete: true, pendingDestination: action.destination };
        case 'COMPLETE_ANIMATION':
          return { ...state, travelTimeComplete: false };
        case 'RESET':
          return { travelTimeComplete: false, pendingDestination: null };
        default:
          return state;
      }
    },
    { travelTimeComplete: false, pendingDestination: null }
  );
  
  const { travelTimeComplete, pendingDestination } = animationState;
  
  // Show onboarding modal for first-time players
  useEffect(() => {
    console.log('🎓 Onboarding check:', { 
      playerLoading, 
      hasPlayer: !!player, 
      hasSeenOnboarding: player?.hasSeenOnboarding, 
      hasMap: !!map,
      hasShownThisSession: hasShownOnboardingRef.current
    });
    
    // Only show once per session AND wait for loading to complete
    if (!playerLoading && !hasShownOnboardingRef.current && player && !player.hasSeenOnboarding && map) {
      console.log('✅ Showing onboarding modal');
      hasShownOnboardingRef.current = true; // Prevent showing again this session
      setShowOnboarding(true);
    }
  }, [playerLoading, player?.hasSeenOnboarding, map]);
  
  // Get current terrain based on player position
  const currentTerrain = React.useMemo(() => {
    if (!map || !playerPosition) return selectedTerrain;
    const hex = map.hexes.get(`${playerPosition.q},${playerPosition.r}`);
    return hex?.terrain ?? selectedTerrain;
  }, [map, playerPosition, selectedTerrain]);
  const [initialZoom, setInitialZoom] = useState<number | undefined>(undefined);
  const [islandCenter, setIslandCenter] = useState<AxialCoord | undefined>(undefined);
  // Derive combat state from PlayerContext (persisted across devices)
  const isInCombat = combatState?.isActive ?? false;
  const combatMonsterName = combatState?.monster?.name ?? '';
  const [showCombatUI, setShowCombatUI] = useState(false); // Force combat UI to show when Fight button clicked
  
  // DEBUG: Log combat state derivation
  useEffect(() => {
    console.log('🎮 App - Combat State Derived:', {
      isInCombat,
      combatMonsterName,
      hasCombatState: !!combatState,
      round: combatState?.round,
      isActive: combatState?.isActive,
      hasPendingEncounter: !!pendingEncounter,
      pendingMonster: pendingEncounter?.monsterName,
    });
  }, [isInCombat, combatMonsterName, combatState, pendingEncounter]);
  const [previousPosition, setPreviousPosition] = useState<AxialCoord | null>(null);
  const [isQuestActive, setIsQuestActive] = useState(false);
  const [nextQuestHex, setNextQuestHex] = useState<{ q: number; r: number } | null>(null);
  
  // Track pending travel that should start after story loads
  const [pendingTravelStart, setPendingTravelStart] = useState<{
    destination: AxialCoord;
    route: AxialCoord[];
    travelTimeMs: number;
  } | null>(null);
  
  // Use ref for synchronous guard checks (state updates are async and cause race conditions)
  const previousPositionRef = useRef<AxialCoord | null>(null);
  
  // Store travel route for marking hexes as explored when animation completes
  const travelRouteRef = useRef<AxialCoord[] | null>(null);
  
  // Track initial page load to prevent story regeneration on reload
  const isInitialLoadRef = useRef<boolean>(true);
  
  // AI Stories toggle state (dev feature)
  const [aiStoriesEnabled, setAiStoriesEnabled] = useState<boolean>(() => {
    return localStorage.getItem('aiStoriesEnabled') !== 'false';
  });
  
  // Initialize monster using bestiary (for 1 player party)
  const [monster] = useState<Monster>(() => {
    const monsterData = initializeMonster('Fairy', 1);
    return monsterData ? {
      id: '1',
      name: monsterData.name,
      currentHp: monsterData.currentHp,
      maxHp: monsterData.maxHp,
      attack: monsterData.attack,
      imagePath: '/assets/monsters/fairy-radiant.png',
    } : {
      id: '1',
      name: 'Fairy',
      currentHp: 2,
      maxHp: 2,
      attack: 3,
      imagePath: '/assets/monsters/fairy-radiant.png',
    };
  });
  
  // combatState now comes from PlayerContext (see usePlayer destructuring above)
  const [legacyCombatState] = useState<CombatState>({
    isActive: true,
    player: {
      id: '1',
      name: 'Alex Lux',
      stamina: 16,
      maxStamina: 16,
      hp: 60,
      maxHp: 60,
      rations: 10,
      maxRations: 10,
      gold: 10,
    },
    party: [{
      id: '1',
      name: 'Alex Lux',
      stamina: 16,
      maxStamina: 16,
      hp: 60,
      maxHp: 60,
      rations: 10,
      maxRations: 10,
      gold: 10,
    }],
    partySize: 1,
    monster: {
      id: '1',
      name: 'Fairy',
      currentHp: 2,
      maxHp: 2,
      attack: 3,
      imagePath: '/assets/monsters/fairy-radiant.png',
    },
    playerRolls: [2, 6],
    partyTotal: 8,
    monsterAttack: 3,
    damage: 5,
    winner: 'party',
    storyLines: [
      'You encounter a luminous fairy.',
      'You approach it. It glows brighter'
    ],
    round: 1,
    canFlee: true,
  });
  
  // Constants for view transitions
  const TRANSITION_ZOOM_THRESHOLD = 4.8; // When to transition from micro map to action screen

  // Click validation interceptor - validates stale data before gameplay actions
  const { isValidating } = useClickValidation({
    player,
    playerPosition,
    combatState,
    pendingEncounter,
    activeQuestId,
    questState,
    travelState,
    updatePosition,
    updatePlayer,
    markHexesExplored,
    setCombatState,
    setPendingEncounter,
    setActiveQuestId,
    setQuestState,
    setActionCompletionMessage,
    containerRef: gameContainerRef,
  });
  
  // Stable callback functions to prevent ActionUI from remounting
  const handleFightClick = useCallback(() => {
    console.log('🔍 handleFightClick called');
    console.log('   pendingEncounter:', pendingEncounter);
    console.log('   explorationState.currentStory:', explorationState.currentStory);
    console.log('   isInCombat:', isInCombat);
    console.log('   combatState:', combatState);
    
    if (pendingEncounter || explorationState.currentStory?.hasMonster) {
      // Clear any action completion message when entering combat
      setActionCompletionMessage(null);
      // Force combat UI to show
      setShowCombatUI(true);
      // pendingEncounter is already set by useExploration or loaded from Firestore
      // CombatUI will mount and call startCombat, which sets combatState in context
      console.log(`⚔️ Fight button clicked - monster: ${pendingEncounter?.monsterName || explorationState.currentStory?.monsterName}`);
      console.log('   CombatUI will now render');
    } else {
      console.log('❌ No monster to fight!');
    }
  }, [explorationState.currentStory, pendingEncounter, isInCombat, combatState]);

  const triggerStoryGeneration = useCallback(async (hex: AxialCoord, isInitialEntry: boolean = false, forcedMonsterName?: string): Promise<any> => {
    if (!map) {
      console.warn('⚠️ triggerStoryGeneration: No map available');
      return null;
    }
    
    const hexData = map.hexes.get(`${hex.q},${hex.r}`);
    if (!hexData) {
      console.warn(`⚠️ triggerStoryGeneration: No hex data for (${hex.q}, ${hex.r})`);
      return null;
    }
    
    console.log('📖 Triggering story generation for hex:', hex);
    console.log(`   isInitialEntry: ${isInitialEntry}`);
    console.log(`   forcedMonsterName: ${forcedMonsterName || 'none'}`);
    console.log(`   terrain: ${hexData.terrain}`);
    
    try {
      // Pass forcedMonsterName to handleHexEntry so it bypasses its own random check
      // This ensures the monster we detected during travel is used in story generation
      const story = await handleHexEntry(hex, hexData.terrain, hexData, isInitialEntry, forcedMonsterName);
      if (story) {
        console.log(`📖 TRAVEL DEBUG - Story returned from handleHexEntry:`, {
          hasMonster: story.hasMonster,
          monsterName: story.monsterName,
          forcedMonsterName: forcedMonsterName,
          matches: story.monsterName === forcedMonsterName,
          hasText: !!story.text,
          textLength: story.text?.length
        });
        
        // Story text is now managed by explorationState
        // Monster encounter is now persisted in pendingEncounter by useExploration
        if (story.hasMonster && story.monsterName) {
          console.log(`🐉 Monster encounter in story: ${story.monsterName}`);
        } else if (forcedMonsterName) {
          console.warn(`⚠️ TRAVEL DEBUG - Expected monster ${forcedMonsterName} but story has no monster!`);
          console.warn(`   This suggests handleHexEntry's encounter check didn't match our travel encounter check`);
        }
        return story;
      } else {
        console.warn(`⚠️ TRAVEL DEBUG - handleHexEntry returned null/undefined`);
      }
      return null;
    } catch (error) {
      console.error('❌ Error in triggerStoryGeneration:', error);
      return null;
    }
  }, [map, handleHexEntry]);

  // Handle hex arrival during travel - check for monster encounters
  // Use a ref to track if we're currently processing a hex to prevent duplicate calls
  const processingHexRef = useRef<string | null>(null);
  
  const handleHexArrival = useCallback(async (hex: AxialCoord) => {
    // CRITICAL: Check travelState at the start - if it's null, travel was already stopped
    // This prevents any encounter checks after travel has been stopped
    if (!travelState || !map) {
      console.log('🛑 TRAVEL DEBUG - handleHexArrival called but travelState is null, aborting');
      return;
    }
    
    // Prevent duplicate calls for the same hex
    const hexKey = `${hex.q},${hex.r}`;
    if (processingHexRef.current === hexKey) {
      console.log(`⏭️ TRAVEL DEBUG - Already processing hex (${hex.q}, ${hex.r}), skipping duplicate call`);
      return;
    }
    
    // Prevent multiple calls for the same hex
    const currentHexIndex = travelState.currentRouteIndex + 1;
    if (currentHexIndex >= travelState.route.length) {
      // Already at destination
      return;
    }
    
    const expectedHex = travelState.route[currentHexIndex];
    if (expectedHex.q !== hex.q || expectedHex.r !== hex.r) {
      // Not the expected hex - ignore
      return;
    }
    
    // Mark this hex as being processed
    processingHexRef.current = hexKey;
    
    try {
      // Get hex data for terrain
      const hexData = map.hexes.get(`${hex.q},${hex.r}`);
      if (!hexData) {
        console.warn(`⚠️ Hex data not found for (${hex.q}, ${hex.r})`);
        return;
      }
      
      // DEBUG: Log hex arrival
      console.log(`🚶 TRAVEL DEBUG - Arrived at hex (${hex.q}, ${hex.r}) during travel`);
      console.log(`   Route progress: ${currentHexIndex + 1} of ${travelState.route.length}`);
      console.log(`   Terrain: ${hexData.terrain}`);
      console.log(`   Start hex: (${travelState.route[0].q}, ${travelState.route[0].r})`);
      console.log(`   End hex: (${travelState.destinationHex.q}, ${travelState.destinationHex.r})`);
      
      // Check for monster encounter at this hex
      const { checkMonsterEncounter } = require('./utils/encounterCheck');
      const hasMonster = checkMonsterEncounter(hexData.terrain);
      
      console.log(`🎲 TRAVEL DEBUG - Encounter check result:`, {
        hex: `(${hex.q}, ${hex.r})`,
        terrain: hexData.terrain,
        hasMonster,
        encounterRate: '20% (from encounterCheck)'
      });
      
      if (hasMonster) {
        // Monster encountered - stop travel at this hex
        console.log(`🐉 TRAVEL DEBUG - MONSTER ENCOUNTERED at hex (${hex.q}, ${hex.r})`);
        console.log(`   ⚠️ Travel stopped at encounter hex - ALL FURTHER CHECKS CANCELLED`);
        console.log(`   Start hex was: (${travelState.route[0].q}, ${travelState.route[0].r})`);
        console.log(`   End hex was: (${travelState.destinationHex.q}, ${travelState.destinationHex.r})`);
        console.log(`   Encounter hex: (${hex.q}, ${hex.r})`);
        console.log(`   Remaining hexes in route will NOT be checked for encounters`);
        
        // CRITICAL: Cancel any pending animation before stopping travel
        // This prevents the animation from continuing to the original destination
        dispatchAnimation({ type: 'RESET' });
        console.log(`   🛑 Animation cancelled - player stops at encounter hex`);
        
        // CRITICAL: Update previousPositionRef BEFORE stopping travel
        // This prevents the hex entry useEffect from regenerating story when position updates
        previousPositionRef.current = hex;
        setPreviousPosition(hex);
        console.log(`📍 TRAVEL DEBUG - Updated previousPositionRef to encounter hex (${hex.q}, ${hex.r})`);
        
        // Stop travel and update position to encounter hex
        // This sets travelState to null, which will stop all interval checks
        stopTravelAtHex(hex);
        
        // Select monster BEFORE story generation (same as exploration does)
        const { selectExplorationMonster } = require('./data/bestiary');
        const selectedMonster = selectExplorationMonster(hexData.terrain);
        console.log(`👹 TRAVEL DEBUG - Selected monster for encounter: ${selectedMonster}`);
        console.log(`   Terrain: ${hexData.terrain}`);
        
        // Trigger story generation with FORCED monster
        // IMPORTANT: Wait for story generation to complete before continuing
        const isInitialEntry = false; // Not initial entry during travel
        console.log(`📖 TRAVEL DEBUG - Starting story generation for encounter hex (${hex.q}, ${hex.r})`);
        console.log(`   Forcing monster: ${selectedMonster}`);
        const story = await triggerStoryGeneration(hex, isInitialEntry, selectedMonster);
        if (story) {
          console.log(`✅ TRAVEL DEBUG - Story generation completed:`, {
            hasMonster: story.hasMonster,
            monsterName: story.monsterName,
            expectedMonster: selectedMonster,
            matches: story.monsterName === selectedMonster,
            hasText: !!story.text
          });
          
          // Wait a bit for React state to update, then check pendingEncounter
          // setPendingEncounter is async, so we need to wait for the state update
          setTimeout(() => {
            console.log(`🔍 TRAVEL DEBUG - Checking pendingEncounter after story generation (delayed check):`, {
              hasPendingEncounter: !!pendingEncounter,
              pendingMonsterName: pendingEncounter?.monsterName,
              expectedMonster: selectedMonster,
              explorationStateStory: explorationState.currentStory?.hasMonster,
              explorationStateMonster: explorationState.currentStory?.monsterName
            });
          }, 100);
        } else {
          console.warn(`⚠️ TRAVEL DEBUG - Story generation returned null/undefined`);
        }
      } else {
        // No monster - advance to next hex in route
        console.log(`✅ TRAVEL DEBUG - No monster at (${hex.q}, ${hex.r}) - continuing travel`);
        
        // Double-check travelState is still valid before advancing
        // (in case it was cleared by another action)
        if (!travelState) {
          console.log('🛑 TRAVEL DEBUG - Travel state cleared before advance, aborting');
          return;
        }
        
        advanceTravelToHex(hex);
        
        // Check if we've reached the final destination after advancing
        // Note: advanceTravelToHex updates currentRouteIndex, so check the new state
        // We'll check this on the next hex arrival or when final arrival time is reached
      }
    } finally {
      // Clear processing flag after a short delay to allow state updates
      setTimeout(() => {
        processingHexRef.current = null;
      }, 100);
    }
  }, [travelState, map, stopTravelAtHex, advanceTravelToHex, triggerStoryGeneration]);

  const handleTravelComplete = useCallback(() => {
    // CRITICAL: Check if travel was already stopped (e.g., by monster encounter)
    // If travelState is null, travel was stopped and we shouldn't trigger animation
    if (!travelState) {
      return;
    }
    
    // Get final destination for animation
    const destinationHex = travelState.destinationHex;
    
    // CRITICAL: Clear travel state immediately to prevent infinite loop
    if (destinationHex) {
      cancelTravel(); // Clear the timer state
      
      // Note: START_ANIMATION was already dispatched when travel started
      // Just trigger story generation now
      
      // Trigger story generation immediately
      setTimeout(() => {
        const isInitialEntry = previousPosition === null;
        triggerStoryGeneration(destinationHex, isInitialEntry);
      }, 0);
    }
  }, [travelState, cancelTravel, previousPosition, triggerStoryGeneration, pendingDestination]);

  const handleHexSelect = useCallback((coord: AxialCoord) => {
    setSelectedHex(coord);
    if (map) {
      const hex = map.hexes.get(`${coord.q},${coord.r}`);
      if (hex) {
        setSelectedTerrain(hex.terrain);
      }
    }
  }, [map]);

  // Memoize computed props to prevent MapContainer from remounting
  const plannedRouteForMap = React.useMemo(() => {
    return travelState?.route || proposedRoute || undefined;
  }, [travelState?.route, proposedRoute]);

  // Animation ready: triggers when travel is complete, loading is done, AND story exists
  // FIX: Wait for story to prevent player moving before narration loads
  const isAnimationReady = React.useMemo(() => {
    const ready = travelTimeComplete && !explorationState.isLoading && !!explorationState.currentStory;
    return ready;
  }, [travelTimeComplete, explorationState.isLoading, explorationState.currentStory, pendingDestination]);

  const hasUnresolvedMonsterForMap = React.useMemo(() => {
    return explorationState.currentStory?.hasMonster && !isInCombat;
  }, [explorationState.currentStory?.hasMonster, isInCombat]);

  const initialCenterForMap = React.useMemo(() => {
    return islandCenter || map?.startPosition || playerPosition;
  }, [islandCenter, map?.startPosition, playerPosition]);

  // Automatic view transition based on zoom level
  useEffect(() => {
    if (viewMode === 'map' && currentZoom >= TRANSITION_ZOOM_THRESHOLD) {
      setViewMode('action');
    }
  }, [viewMode, currentZoom, TRANSITION_ZOOM_THRESHOLD]);
  
  // Sync audio URL with shared audio player when story changes
  useEffect(() => {
    console.log('═══════════════════════════════════════════════════════');
    console.log('🎵 [APP_EXPLORATION_SYNC] Audio sync useEffect TRIGGERED');
    console.log('   Timestamp:', new Date().toISOString());
    console.log('   explorationState.currentStory?.audioUrl:', explorationState.currentStory?.audioUrl);
    console.log('   This is EXPLORATION audio from terrain narrative');
    console.log('═══════════════════════════════════════════════════════');
    
    if (explorationState.currentStory?.audioUrl) {
      console.log('📞 [APP_EXPLORATION_SYNC] Calling audioPlayer.setAudioUrl with EXPLORATION audio:', explorationState.currentStory.audioUrl);
      audioPlayer.setAudioUrl(explorationState.currentStory.audioUrl);
      console.log('✅ [APP_EXPLORATION_SYNC] audioPlayer.setAudioUrl call completed');
    }
    // NOTE: audioPlayer should NOT be in dependencies - it causes infinite loops!
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [explorationState.currentStory?.audioUrl]);
  
  // Reset world selection when user logs out
  useEffect(() => {
    if (!user) {
      setSelectedWorld(null);
    }
  }, [user]);
  
  // Helper function to trigger story generation for a hex
  // Handle hex click from mini isometric map (move player or show modal)
  const handleMiniMapHexClick = async (coord: AxialCoord) => {
    if (!map) return;
    
    console.log('🗺️ Mini map hex click handler called:', {
      clickedHex: coord,
      playerPosition,
      stamina: player.stamina,
    });
    
    // Check for unresolved monster encounter or combat state
    const hasUnresolvedMonster = explorationState.currentStory?.hasMonster && !isInCombat;
    
    // Don't allow movement during these states
    if (isTraveling() || isForaging() || isResting() || isInCombat || hasUnresolvedMonster || isQuestActive) {
      console.log('❌ Movement blocked in App.tsx:', { 
        isTraveling: isTraveling(), 
        isForaging: isForaging(),
        isResting: isResting(),
        isInCombat,
        hasUnresolvedMonster,
        isQuestActive
      });
      return;
    }
    
    // ===== DAILY RESET CHECK + STATE SYNC =====
    // Check if player needs daily reset and validate state synchronization
    try {
      console.log('🔄 Checking for daily reset and state sync...');
      console.log('   Current player stamina:', player.stamina);
      console.log('   Current view mode:', viewMode);
      
      // Create client state snapshot for sync validation
      const clientState = createClientStateSnapshot(
        player,
        playerPosition,
        combatState,
        pendingEncounter
      );
      
      const resetResult = await ensurePlayerReady(clientState);
      console.log('   Reset result:', resetResult);
      
      // Check if server detected state mismatch
      if (resetResult.syncRequired && resetResult.gameplayState) {
        console.warn('🔄 State mismatch detected - reloading page to sync state');
        console.log('   Sync issues:', resetResult.syncIssues);
        
        // Reload page to get fresh state from server
        // This is simpler than applying state manually and ensures complete sync
        window.location.reload();
        return;
      }
      
      if (resetResult.resetApplied) {
        console.log('✨ Daily reset applied:', resetResult);
        // Update player state with restored values (silent reset)
        updatePlayer({
          stamina: resetResult.player.stamina,
          hp: resetResult.player.hp,
          lastDailyReset: resetResult.player.lastDailyReset,
          nextResetAvailable: resetResult.player.nextResetAvailable,
          totalDailyResetsClaimed: resetResult.player.totalDailyResetsClaimed,
        });
        // Reset happens silently - no message displayed
      } else if (resetResult.player.stamina === 0) {
        // Out of stamina - show message
        console.log('❌ Out of stamina - showing message');
        const message = getOutOfStaminaMessage(resetResult.hoursUntilReset);
        console.log('   Message content:', message);
        console.log('   Setting actionCompletionMessage...');
        setActionCompletionMessage(message);
        console.log('   Returning early (blocking movement)');
        return;
      } else {
        console.log('✅ Player has stamina:', resetResult.player.stamina, '- continuing with movement');
        
        // Sync player state from server if there's a mismatch
        // This handles cases where reset happened earlier but client state is stale
        if (resetResult.player.stamina !== player.stamina || 
            resetResult.player.hp !== player.hp ||
            resetResult.player.maxStamina !== player.maxStamina ||
            resetResult.player.maxHp !== player.maxHp) {
          console.log('🔄 Syncing player state from server (state mismatch detected)');
          console.log('   Client stamina:', player.stamina, '→ Server stamina:', resetResult.player.stamina);
          console.log('   Client hp:', player.hp, '→ Server hp:', resetResult.player.hp);
          updatePlayer({
            stamina: resetResult.player.stamina,
            hp: resetResult.player.hp,
            maxStamina: resetResult.player.maxStamina,
            maxHp: resetResult.player.maxHp,
          });
        }
      }
    } catch (error) {
      console.error('❌ Error checking daily reset:', error);
      // Continue with movement even if reset check fails
    }
    
    // Calculate distance
    const distance = hexDistance(coord, playerPosition);
    
    // If clicking same hex, ignore
    if (distance === 0) {
      return;
    }
    
    // If adjacent hex (distance 1), do instant travel like before
    if (distance === 1 && player.stamina > 0) {
      const travelTimeMs = FEATURES.TRAVEL_TIMERS ? getTravelTimeSetting() : 0;
      console.log('🚶 Starting adjacent travel - generating story first:', {
        from: playerPosition,
        to: coord,
        travelTimeMs,
        stamina: player.stamina,
      });
      setActionCompletionMessage(null);
      
      // Clear current story and trigger generation for destination
      clearStory();
      const isInitialEntry = previousPosition === null;
      triggerStoryGeneration(coord, isInitialEntry);
      
      // Store pending travel - will start when story loads
      const route = [playerPosition, coord];
      travelRouteRef.current = route; // Store for marking hexes as explored when animation completes
      setPendingTravelStart({
        destination: coord,
        route,
        travelTimeMs
      });
      
      console.log('📖 Story generation started, waiting for completion before travel');
      return;
    }
    
    // If out of stamina, show message (fallback check)
    if (player.stamina === 0) {
      console.log('❌ FALLBACK: Player still has 0 stamina after reset check');
      const message = getOutOfStaminaMessage();
      console.log('   Setting fallback message:', message);
      setActionCompletionMessage(message);
      return;
    }
    
    // For distance > 1, find path and show modal
    if (distance > 1) {
      const route = findShortestPath(playerPosition, coord, map);
      
      if (!route) {
        console.log('❌ No path found to:', coord);
        // Could show a message to user here
        return;
      }
      
      console.log('🛤️ Found route:', route);
      
      // Get destination terrain
      const destHex = map.hexes.get(`${coord.q},${coord.r}`);
      if (!destHex) return;
      
      // Show travel modal
      setProposedRoute(route);
      setModalDestination(coord);
      setShowTravelModal(true);
    }
  };
  
  // Handle travel modal confirmation
  const handleTravelConfirm = () => {
    if (!proposedRoute) return;
    
    const destination = proposedRoute[proposedRoute.length - 1];
    console.log('✅ Starting multi-hex travel immediately (no pre-generation):', proposedRoute);
    setActionCompletionMessage(null);
    
    // Clear current story - new story will be generated when player arrives OR encounters monster
    clearStory();
    
    // Store route for marking hexes as explored when animation completes
    travelRouteRef.current = proposedRoute;
    
    // Start travel immediately (DON'T wait for story - might encounter monster!)
    // Story will be generated either:
    // - When player encounters a monster mid-journey (via handleTravelArrival)
    // - When player arrives at destination safely (via handleTravelComplete)
    const travelTimeMs = FEATURES.TRAVEL_TIMERS ? getTravelTimeSetting() : 0;
    startTravel(proposedRoute, travelTimeMs);
    
    console.log('🚶 Multi-hex travel started - story will generate on arrival or monster encounter');
    
    // Close modal
    setShowTravelModal(false);
    setProposedRoute(null);
    setModalDestination(null);
  };
  
  // Handle travel modal cancellation
  const handleTravelCancel = () => {
    setShowTravelModal(false);
    setProposedRoute(null);
    setModalDestination(null);
  };
  
  // Handle "View Full Map" button click from mini isometric map
  const handleViewMapClick = () => {
    setViewMode('map');
    setInitialZoom(2.0); // Open map at comfortable zoom level
  };
  
  // Handle player marker click - navigate to action screen
  const handlePlayerMarkerClick = () => {
    console.log('Player marker clicked - switching to action screen');
    setViewMode('action');
  };
  
  // Handle foraging start
  const handleForageClick = () => {
    if (!currentTerrain) {
      console.warn('Cannot forage: no current terrain');
      return;
    }
    
    // Check conditions
    if (isTraveling()) {
      console.warn('Cannot forage while traveling');
      return;
    }
    
    if (isForaging()) {
      console.warn('Already foraging');
      return;
    }
    
    if (isInCombat) {
      console.warn('Cannot forage while in combat');
      return;
    }
    
    if (player.rations >= player.maxRations) {
      console.warn('Cannot forage: ration storage full');
      return;
    }
    
    const yield_amount = FORAGING_YIELDS[currentTerrain];
    if (yield_amount === 0) {
      console.warn('Cannot forage in this terrain:', currentTerrain);
      return;
    }
    
    // Clear any action completion message when starting a new action
    setActionCompletionMessage(null);
    
    const foragingTimeMs = getForagingTimeSetting();
    console.log('🌾 Starting foraging in terrain:', currentTerrain, 'expected yield:', yield_amount, 'duration:', foragingTimeMs);
    startForaging(currentTerrain, foragingTimeMs);
  };
  
  // Handle foraging completion
  const handleForagingComplete = () => {
    if (!foragingState) return;
    
    const terrainType = foragingState.terrainType;
    const rationYield = FORAGING_YIELDS[terrainType] || 0;
    
    console.log('✅ Foraging complete! Terrain:', terrainType, 'Rations gained:', rationYield);
    
    if (rationYield > 0) {
      addRations(rationYield);
      
      // Show completion message
      const rationWord = rationYield === 1 ? 'ration' : 'rations';
      setActionCompletionMessage(`Your foraging expedition was successful! You gained ${rationYield} ${rationWord}.`);
    }
    
    completeForaging();
  };
  
  // Handle rest/heal action
  const handleRestHealClick = () => {
    if (isTraveling()) {
      console.warn('Cannot rest/heal while traveling');
      return;
    }
    
    if (isForaging()) {
      console.warn('Cannot rest/heal while foraging');
      return;
    }
    
    if (isResting()) {
      console.warn('Already resting');
      return;
    }
    
    if (isInCombat) {
      console.warn('Cannot rest/heal while in combat');
      return;
    }
    
    if (player.hp >= player.maxHp) {
      console.warn('HP is already full');
      return;
    }
    
    // Clear any action completion message when starting a new action
    setActionCompletionMessage(null);
    
    console.log('💤 Rest/Heal action triggered');
    const restingTimeMs = getForagingTimeSetting(); // Use same duration setting as foraging
    startResting(restingTimeMs);
  };
  
  // Handle resting completion
  const handleRestingComplete = () => {
    if (!restingState) return;
    
    // Award random 1-10 HP (never exceeding max)
    const hpGained = Math.floor(Math.random() * 10) + 1; // Random from 1 to 10
    console.log(`💚 Resting complete! Gained ${hpGained} HP`);
    heal(hpGained);
    
    // Show completion message
    const hpWord = hpGained === 1 ? 'hit point' : 'hit points';
    setActionCompletionMessage(`You awaken feeling refreshed! You recovered ${hpGained} ${hpWord}.`);
    
    completeResting();
  };
  
  // Handle ration consumption
  const handleRationClick = () => {
    if (player.stamina >= player.maxStamina) {
      console.warn('Stamina is already full');
      return;
    }
    
    if (player.rations <= 0) {
      console.warn('No rations available');
      return;
    }
    
    console.log('🍖 Consuming 1 ration to restore 1 stamina');
    consumeRations(1);
  };
  
  // Start travel when story generation completes
  useEffect(() => {
    if (!pendingTravelStart) {
      return; // No pending travel
    }
    if (explorationState.isLoading) {
      return; // Wait for story to finish loading
    }
    if (!explorationState.currentStory) {
      return; // Wait for story to be generated
    }
    
    console.log('✅ Story loaded, starting travel to:', pendingTravelStart.destination);
    
    // Clear the pending travel state
    const { destination, route, travelTimeMs } = pendingTravelStart;
    setPendingTravelStart(null);
    
    // Set animation state
    dispatchAnimation({ type: 'START_ANIMATION', destination });
    
    // Start the actual travel
    startTravel(route, travelTimeMs);
  }, [pendingTravelStart, explorationState.isLoading, explorationState.currentStory, startTravel, dispatchAnimation]);
  
  // Coordinate animation: when travel time is complete AND story is ready, trigger animation
  useEffect(() => {
    if (!travelTimeComplete || !pendingDestination) {
      return;
    }
    if (explorationState.isLoading) {
      return; // Wait for story to finish loading
    }
    if (!explorationState.currentStory) {
      return; // Wait for story to be generated
    }
    
    // Both travel and story are ready - animation will be triggered by map components
    // They will call onAnimationComplete when done, which will call completeTravel()
  }, [travelTimeComplete, pendingDestination, explorationState.isLoading, explorationState.currentStory]);
  
  // Handle animation completion - update position after animation
  const handleAnimationComplete = useCallback(() => {
    console.log('✅ handleAnimationComplete called:', { pendingDestination, playerPosition });
    
    if (!pendingDestination) {
      console.warn('⚠️ handleAnimationComplete: No pendingDestination!');
      return;
    }
    
    // Batch all state updates together to reduce flickering
    // Use React's automatic batching (React 18+) or setTimeout to batch updates
    setTimeout(() => {
      console.log('📍 Updating position to:', pendingDestination);
      
      // Update previousPosition SYNCHRONOUSLY using ref to prevent race condition
      // State updates (setPreviousPosition) are async and the useEffect might fire before they're applied
      // Using ref ensures the guard check works immediately
      previousPositionRef.current = pendingDestination;
      setPreviousPosition(pendingDestination);
      
      // Mark hexes as explored NOW (when animation completes)
      // This prevents the "flashing" effect where hexes highlight before the player marker arrives
      if (travelRouteRef.current) {
        const hexesToExplore: AxialCoord[] = [];
        
        // Mark all hexes along route and their neighbors as explored
        travelRouteRef.current.forEach(hex => {
          hexesToExplore.push(hex);
          
          // Also explore adjacent hexes
          const neighbors = [
            { q: hex.q + 1, r: hex.r },
            { q: hex.q - 1, r: hex.r },
            { q: hex.q, r: hex.r + 1 },
            { q: hex.q, r: hex.r - 1 },
            { q: hex.q + 1, r: hex.r - 1 },
            { q: hex.q - 1, r: hex.r + 1 },
          ];
          
          hexesToExplore.push(...neighbors);
        });
        
        console.log(`🗺️ Marking ${hexesToExplore.length} hexes as explored (animation complete)`);
        markHexesExplored(hexesToExplore);
        
        // Clear the ref
        travelRouteRef.current = null;
      }
      
      // Now update the actual position
      // Note: travelState was already cleared in onTravelComplete to prevent infinite loop
      // So we update position directly instead of calling completeTravel()
      updatePosition(pendingDestination);
      
      console.log('✅ Position updated to:', pendingDestination);
      
      // Reset animation states using reducer
      dispatchAnimation({ type: 'RESET' });
    }, 0);
  }, [pendingDestination, updatePosition, playerPosition, markHexesExplored]);
  
  // Handle hex entry for exploration mode
  useEffect(() => {
    if (!map || !playerPosition) {
      return;
    }
    
    // Don't generate story if player data is still loading from Firestore
    // This prevents generating new encounters when position changes from default to loaded value
    if (playerLoading) {
      console.log('🚫 Hex entry useEffect: Skipping - player data still loading from Firestore');
      return;
    }
    
    // Skip story generation on initial page load (prevents regenerating story on reload)
    if (isInitialLoadRef.current) {
      console.log('🚫 Hex entry useEffect: Skipping - initial page load');
      isInitialLoadRef.current = false; // Clear flag after first run
      previousPositionRef.current = playerPosition; // Set so next move is detected
      setPreviousPosition(playerPosition);
      return;
    }
    
    // Don't generate story if respawning from defeat
    if (isRespawning) {
      console.log('🚫 Hex entry useEffect: Skipping - respawning from defeat');
      return;
    }
    
    // Don't generate exploration content during quests
    if (isQuestActive) {
      console.log('🚫 Hex entry useEffect: Skipping - quest active');
      return;
    }
    
    // Don't generate content if currently traveling
    if (travelState) {
      console.log('🚫 Hex entry useEffect: Skipping - currently traveling');
      return;
    }
    
    // Don't generate if we're in the middle of completing a travel (story already generated)
    // This prevents duplicate story generation when completeTravel() updates the position
    if (pendingDestination && 
        pendingDestination.q === playerPosition.q && 
        pendingDestination.r === playerPosition.r) {
      console.log('🚫 Hex entry useEffect: Skipping - completing travel');
      return; // We're completing a travel, story was already generated in onTravelComplete
    }
    
    // Check if position actually changed (using REF for synchronous check!)
    // Using previousPosition state would fail due to async setState
    if (previousPositionRef.current && 
        previousPositionRef.current.q === playerPosition.q && 
        previousPositionRef.current.r === playerPosition.r) {
      // Position hasn't changed - but if we have a pending encounter or story, don't regenerate
      // This prevents regeneration when travel stops at an encounter hex
      console.log('🚫 Hex entry useEffect: Position unchanged, checking for existing encounter/story:', {
        hasPendingEncounter: !!pendingEncounter,
        pendingMonster: pendingEncounter?.monsterName,
        hasStory: !!explorationState.currentStory,
        storyHasMonster: explorationState.currentStory?.hasMonster
      });
      if (pendingEncounter || explorationState.currentStory) {
        console.log('🚫 Skipping story generation - same position, encounter/story already exists');
        return;
      }
      return; // Position hasn't changed
    }
    
    // SYNC FIX: Don't generate new story if there's already a pending encounter
    // This prevents Tab 2 from overwriting Tab 1's monster encounter on load
    if (pendingEncounter) {
      console.log('🚫 Skipping story generation - pendingEncounter already exists:', pendingEncounter);
      return;
    }
    
    const hexData = map.hexes.get(`${playerPosition.q},${playerPosition.r}`);
    if (!hexData) return;
    
    // Clear any action completion message when moving to a new hex
    setActionCompletionMessage(null);
    
    // Trigger exploration entry (pass true for initial entry if no previous position)
    const isInitialEntry = previousPosition === null;
    handleHexEntry(playerPosition, hexData.terrain, hexData, isInitialEntry).then((story) => {
      if (story) {
        // Story text is now managed by explorationState (no need for local setStoryText)
        
        // Handle monster encounter - pendingEncounter is already set by useExploration
        // Keep player on map view so they can see the story and Fight button
        if (story.hasMonster && story.monsterName) {
          console.log(`🐉 Monster encounter in story: ${story.monsterName}`);
          // Stay on map view - the StoryContainer has the Fight button
        }
      }
    });
    
    // Update both state and ref for position tracking
    previousPositionRef.current = playerPosition;
    setPreviousPosition(playerPosition);
  }, [playerPosition, map, handleHexEntry, isInCombat, travelState, pendingDestination, isRespawning, isQuestActive, playerLoading]);

  // Auto-start next quest when player arrives at next quest hex
  useEffect(() => {
    if (nextQuestHex && playerPosition && 
        playerPosition.q === nextQuestHex.q && 
        playerPosition.r === nextQuestHex.r) {
      console.log(`📍 Player arrived at next quest hex (${nextQuestHex.q}, ${nextQuestHex.r})`);
      console.log('🎬 Auto-starting next quest...');
      
      // Clear the highlight
      setNextQuestHex(null);
      
      // TODO: Trigger quest start automatically
      // For now, this will be handled by clicking the Quest medallion
      // Future: Call questManager.beginCampaign automatically
      
      // Show notification
      setActionCompletionMessage('You have arrived at the next quest location! Click the Quest medallion to begin.');
    }
  }, [playerPosition, nextQuestHex]);

  // Respawn player at their spawn hex (for testing/debugging)
  const respawnAtRandomSettlement = useCallback(async () => {
    if (!user || !map) {
      console.error('Cannot respawn: user or map not available');
      return;
    }
    
    try {
      // Use existing spawn hex (check both new and old field names), or select one if somehow missing
      const spawnHex = player.spawnHex || (player as any).homeHex || selectRandomSpawnHex(map);
      console.log(`🏠 Respawning at spawn hex: (${spawnHex.q}, ${spawnHex.r})`);
      
      const playerRef = doc(db, 'players', user.uid);
      await updateDoc(playerRef, {
        // Only update position and HP - DO NOT overwrite spawnHex
        position: spawnHex,
        hp: player.maxHp,
      });
      
      updatePlayer({ hp: player.maxHp });
      updatePosition(spawnHex);
      
      setActionCompletionMessage(`You have respawned at your settlement (${spawnHex.q}, ${spawnHex.r})!`);
      console.log('✅ Respawn complete!');
    } catch (error) {
      console.error('❌ Error respawning:', error);
    }
  }, [user, map, player, updatePlayer, updatePosition]);
  
  // Expose respawn function to window for console access
  useEffect(() => {
    if (typeof window !== 'undefined') {
      (window as any).respawnPlayer = respawnAtRandomSettlement;
    }
  }, [respawnAtRandomSettlement]);

  // Generate map on authentication
  useEffect(() => {
    if (user && !map) {
      setIsGenerating(true);
      
      // Use setTimeout to allow UI to update before heavy computation
      setTimeout(() => {
        console.log('Starting map generation...');
        const startTime = performance.now();
        
        // Generate 288x162 map (16:9 aspect ratio, ~46k hexes with ~40k land)
        const generatedMap = generateMap(288, 162, 'storyworld-poc-v1');
        
        const endTime = performance.now();
        console.log(`Map generation completed in ${(endTime - startTime).toFixed(2)}ms`);
        
        // Log terrain distribution
        const distribution = getTerrainDistribution(generatedMap);
        console.log('Terrain distribution:', distribution);
        
        // Calculate zoom level and center to fit entire island on screen
        // Use window size for better fit (canvas will be sized to container)
        // Account for UI elements that take up space
        const uiWidth = 300; // Approximate width of side panels
        const uiHeight = 100; // Approximate height of top/bottom UI
        const canvasWidth = Math.max(400, window.innerWidth - uiWidth); // Ensure minimum width
        const canvasHeight = Math.max(400, window.innerHeight - uiHeight); // Ensure minimum height
        const fitResult = calculateFitToScreenZoom(
          generatedMap,
          canvasWidth,
          canvasHeight,
          30, // hexSize
          0.05 // 5% padding - minimal padding to show more island
        );
        console.log('Calculated fit-to-screen zoom:', fitResult.zoom, 'center:', fitResult.center);
        setInitialZoom(fitResult.zoom);
        setIslandCenter(fitResult.center);
        
        setMap(generatedMap);
        
        // Initialize/migrate spawn hex
        (async () => {
          try {
            const playerRef = doc(db, 'players', user.uid);
            const playerDoc = await getDoc(playerRef);
            const firestoreData = playerDoc.data();
            
            // Check for spawn hex (prefer spawnHex, fallback to old homeHex field)
            const existingSpawnHex = player.spawnHex || (player as any).homeHex;
            const firestoreSpawnHex = firestoreData?.spawnHex || firestoreData?.homeHex;
            const playerMapSeed = player.mapSeed || firestoreData?.mapSeed;
            
            // Check if map has changed OR player has no map seed tracking yet
            // If no mapSeed exists, treat it as a map change to validate/reassign spawn hex
            const mapChanged = !playerMapSeed || playerMapSeed !== generatedMap.seed;
            
            if (mapChanged && (existingSpawnHex || firestoreSpawnHex)) {
              // Player has spawn hex but map changed OR no mapSeed tracking
              console.log(`\n🗺️ ============ MAP CHANGED / VALIDATION NEEDED ============`);
              console.log(`   Old map seed: ${playerMapSeed || 'NONE'}`);
              console.log(`   New map seed: ${generatedMap.seed}`);
              console.log(`   Current spawn hex: (${(existingSpawnHex || firestoreSpawnHex).q}, ${(existingSpawnHex || firestoreSpawnHex).r})`);
              
              // Validate current spawn hex is still a settlement
              const currentSpawn = existingSpawnHex || firestoreSpawnHex;
              const hexKey = `${currentSpawn.q},${currentSpawn.r}`;
              const hex = generatedMap.hexes.get(hexKey);
              const isValidSettlement = hex && hex.terrain === TerrainType.Settlement;
              
              if (!isValidSettlement) {
                console.log(`   ⚠️ Spawn hex is NO LONGER a settlement! Reassigning...`);
                const newSpawnHex = selectRandomSpawnHex(generatedMap);
                console.log(`   📍 New spawn hex: (${newSpawnHex.q}, ${newSpawnHex.r})`);
                
                await updateDoc(playerRef, {
                  spawnHex: newSpawnHex,
                  mapSeed: generatedMap.seed,
                });
                
                updatePlayer({ spawnHex: newSpawnHex, mapSeed: generatedMap.seed });
                
                // Mark new spawn hex and neighbors as explored
                const neighbors = hexNeighbors(newSpawnHex);
                markHexesExplored([newSpawnHex, ...neighbors]);
                
                console.log(`✅ Player spawn hex reassigned to valid settlement`);
              } else {
                console.log(`   ✅ Spawn hex is still a valid settlement`);
                console.log(`   📝 Updating map seed tracking...`);
                
                await updateDoc(playerRef, {
                  mapSeed: generatedMap.seed,
                });
                
                updatePlayer({ mapSeed: generatedMap.seed });
                console.log(`✅ Map seed tracking updated`);
              }
              console.log(`============================================\n`);
            } else if (!existingSpawnHex && !firestoreSpawnHex) {
              // No spawn hex exists - initialize for new player
              console.log(`🏠 No spawn hex found - initializing new spawn hex`);
              const spawnHex = selectRandomSpawnHex(generatedMap);
              console.log(`   📍 Selected spawn hex: (${spawnHex.q}, ${spawnHex.r})`);
              
              // Check if this is a truly new player
              const isNewPlayer = !firestoreData || !firestoreData.position;
              console.log(`   🔍 Player check: ${isNewPlayer ? 'NEW player' : 'EXISTING player'}`);
              
              const updates: any = { 
                spawnHex,
                mapSeed: generatedMap.seed, // Track map version
              };
              
              // Only set position for truly new players
              if (isNewPlayer) {
                updates.position = spawnHex;
                console.log(`   📍 NEW player - setting initial position to spawn hex`);
              }
              
              await updateDoc(playerRef, updates);
              updatePlayer({ spawnHex, mapSeed: generatedMap.seed });
              
              if (isNewPlayer) {
                updatePosition(spawnHex);
                restoreStamina(999);
              }
              
              // Mark spawn hex and neighbors as explored
              const neighbors = hexNeighbors(spawnHex);
              markHexesExplored([spawnHex, ...neighbors]);
              
              console.log(`✅ Spawn hex initialized`);
            } else if (firestoreData?.homeHex && !firestoreData?.spawnHex) {
              // MIGRATION: Convert old homeHex to spawnHex
              const homeHex = firestoreData.homeHex;
              console.log(`🔄 Migrating homeHex (${homeHex.q}, ${homeHex.r}) to spawnHex`);
              
              // Validate homeHex is still a settlement before migrating
              const hexKey = `${homeHex.q},${homeHex.r}`;
              const hex = generatedMap.hexes.get(hexKey);
              const isValidSettlement = hex && hex.terrain === TerrainType.Settlement;
              
              if (!isValidSettlement) {
                console.log(`   ⚠️ Old homeHex is no longer a settlement! Selecting new spawn...`);
                const newSpawnHex = selectRandomSpawnHex(generatedMap);
                
                await updateDoc(playerRef, {
                  spawnHex: newSpawnHex,
                  mapSeed: generatedMap.seed,
                  homeHex: deleteField(),
                });
                
                updatePlayer({ spawnHex: newSpawnHex, mapSeed: generatedMap.seed });
                console.log(`✅ Migration complete - new spawn hex: (${newSpawnHex.q}, ${newSpawnHex.r})`);
              } else {
                await updateDoc(playerRef, {
                  spawnHex: homeHex,
                  mapSeed: generatedMap.seed,
                  homeHex: deleteField(),
                });
                
                updatePlayer({ spawnHex: homeHex, mapSeed: generatedMap.seed });
                console.log(`✅ Migration complete - spawn hex is now (${homeHex.q}, ${homeHex.r})`);
              }
            } else {
              // Spawn hex exists and map hasn't changed
              const currentSpawn = existingSpawnHex || firestoreSpawnHex;
              console.log(`🏠 Existing spawn hex: (${currentSpawn.q}, ${currentSpawn.r}) - map unchanged`);
            }
          } catch (error) {
            console.error('Error initializing/migrating spawn hex:', error);
          }
        })();
        
        // Use startPosition from map if available, otherwise use center
        const startPos = generatedMap.startPosition || {
          q: Math.floor(generatedMap.width / 2),
          r: Math.floor(generatedMap.height / 2)
        };
        const startHex = generatedMap.hexes.get(`${startPos.q},${startPos.r}`);
        if (startHex) {
          setSelectedHex(startPos);
          setSelectedTerrain(startHex.terrain);
        }
        
        // Update player position to start on land (only if no spawn hex)
        if (generatedMap.startPosition && player.spawnHex) {
          console.log('Map suggests starting position:', generatedMap.startPosition);
          // Mark starting position and neighbors as explored
          const neighbors = [
            { q: generatedMap.startPosition.q + 1, r: generatedMap.startPosition.r },
            { q: generatedMap.startPosition.q - 1, r: generatedMap.startPosition.r },
            { q: generatedMap.startPosition.q, r: generatedMap.startPosition.r + 1 },
            { q: generatedMap.startPosition.q, r: generatedMap.startPosition.r - 1 },
            { q: generatedMap.startPosition.q + 1, r: generatedMap.startPosition.r - 1 },
            { q: generatedMap.startPosition.q - 1, r: generatedMap.startPosition.r + 1 },
          ];
          markHexesExplored([...neighbors]);
        }
        
        setIsGenerating(false);
      }, 100);
    }
  }, [user, map]);

  // Show loading screen while checking authentication
  if (authLoading) {
    return (
      <div className="App">
        <div className="loading-screen">
          <h1>🗺️ Brynlore</h1>
          <div className="loading-spinner"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  // Handle marker selection and close onboarding modal
  const handleMarkerSelect = (markerId: string) => {
    console.log('🎓 handleMarkerSelect called with:', markerId);
    
    if (player) {
      // Update local state - this will automatically save to Firestore via PlayerContext
      updatePlayer({ 
        hasSeenOnboarding: true,
        selectedMarkerIcon: markerId
      });
      console.log('✅ Onboarding completed and marker selected (saving to Firestore automatically)');
    }
  };
  
  // Handle closing onboarding modal
  const handleCloseOnboarding = () => {
    console.log('🎓 handleCloseOnboarding called');
    setShowOnboarding(false);
  };
  
  // Handle leaving game - returns to world selection
  const handleLeaveGame = () => {
    if (isInCombat) {
      const confirmed = window.confirm(
        'You are currently in combat! Leaving now will resolve the combat automatically. Are you sure you want to leave?'
      );
      if (!confirmed) {
        return;
      }
      // Combat will be resolved automatically - just clear the combat state
      setCombatState(null);
      setPendingEncounter(null);
    }
    
    // Return to world selection (stay logged in)
    setSelectedWorld(null);
    navigate('/worlds');
    console.log('🚪 Leaving game, returning to world selection');
  };

  // Main game view handlers
  const handleAction = (action: string) => {
    console.log('Action:', action);
    if (action === 'start-combat') {
      // Start combat with the current monster (default to Troll)
      startCombat(combatMonsterName);
    }
  };

  // Handle combat end
  const handleCombatEnd = async (result: 'victory' | 'defeat' | 'fled') => {
    console.log('Combat ended:', result);
    
    // Get the monster name for the completion message
    const monsterName = combatMonsterName || pendingEncounter?.monsterName || 'creature';
    
    // Handle defeat - respawn at spawn hex
    const spawnHex = player.spawnHex || (player as any).homeHex;
    if (result === 'defeat' && spawnHex && user) {
      console.log(`⚰️ Player defeated - respawning at (${spawnHex.q}, ${spawnHex.r})`);
      
      try {
        // Set respawning flag to prevent normal hex entry story
        setIsRespawning(true);
        
        // Update in Firestore
        const playerRef = doc(db, 'players', user.uid);
        await updateDoc(playerRef, {
          hp: player.maxHp, // Reset to full HP
          position: spawnHex, // Teleport to spawn
        });
        
        // Update local state
        updatePlayer({ hp: player.maxHp });
        updatePosition(spawnHex);
        
        // Clear any pending animations or travel states
        dispatchAnimation({ type: 'RESET' });
        cancelTravel();
        
        // Show respawn message
        setActionCompletionMessage(
          'You were defeated!\n\nYou awaken at your settlement, wounds healed.'
        );
        
        // Reset respawning flag after a brief delay
        setTimeout(() => {
          setIsRespawning(false);
        }, 100);
      } catch (error) {
        console.error('Error respawning player:', error);
        setIsRespawning(false);
      }
    } else if (result === 'victory') {
      // Show victory message
      setActionCompletionMessage(
        `Victory! The ${monsterName} lies defeated.\n\nThe path ahead is clear.`
      );
    } else if (result === 'fled') {
      // Show flee message
      setActionCompletionMessage(
        `You fled from the ${monsterName}.\n\nYou can continue exploring.`
      );
    }
    
    // FIX BUG #2: Clear previous position ref so next move triggers story generation
    previousPositionRef.current = null;
    
    setCombatState(null); // Clear combat state in context
    setPendingEncounter(null); // Clear pending encounter
    setShowCombatUI(false); // Clear combat UI flag to restore story container
    // Clear the story so we don't keep showing the fight button
    clearStory();
    // Stay on action view - don't force back to map
    // setViewMode('map'); // REMOVED: player should stay where they are
  };

  // Start combat (for testing - in real game, this would be triggered by hex encounter)
  const startCombat = (monsterName: string) => {
    // Clear any action completion message when entering combat
    setActionCompletionMessage(null);
    // Set pending encounter to trigger CombatUI rendering
    setPendingEncounter({
      monsterName,
      storyText: `You encounter a ${monsterName}!`,
      audioUrl: null,
      hex: { q: playerPosition.q, r: playerPosition.r }, // Include current position
    });
  };

  // Get party for combat (currently just the single player)
  const getCombatParty = (): Player[] => {
    return [player];
  };

  // Reset HP to full (for testing/debugging)
  const resetHP = () => {
    updatePlayer({ hp: player.maxHp });
  };

  // Reset Stamina to full (for testing/debugging)
  const resetStamina = () => {
    updatePlayer({ stamina: 12, maxStamina: 12 });
  };

  // Toggle AI stories on/off (dev feature to save API costs)
  const toggleAIStories = () => {
    const newValue = !aiStoriesEnabled;
    setAiStoriesEnabled(newValue);
    localStorage.setItem('aiStoriesEnabled', String(newValue));
    console.log(`🤖 AI Stories: ${newValue ? 'ENABLED ✅' : 'DISABLED 🔇'}`);
  };

  // Handle terrain image click - return to map at 50% zoom
  const handleTerrainImageClick = () => {
    setViewMode('map');
    setInitialZoom(0.5);
  };


  // Main game rendering function
  const renderGame = () => (
    <div 
      className="App" 
      ref={gameContainerRef}
      style={{
        pointerEvents: isValidating ? 'none' : 'auto',
        cursor: isValidating ? 'wait' : 'default',
      }}
    >
      {/* Burger Menu - Mobile/Tablet Only */}
      <BurgerMenu
        viewMode={viewMode}
        onMapClick={() => setViewMode('map')}
        onActionClick={() => setViewMode('action')}
        onFullHPClick={resetHP}
        onFullStaminaClick={resetStamina}
        onAIToggleClick={toggleAIStories}
        onSettingsClick={() => setShowSettings(true)}
        onLoreCodexClick={() => setShowLoreCodex(true)}
        onLeaveGameClick={handleLeaveGame}
        isInCombat={isInCombat}
        isQuestActive={isQuestActive}
        aiStoriesEnabled={aiStoriesEnabled}
      />

      {/* View Toggle - Desktop Only */}
      <div className="view-toggle desktop-only">
        <button 
          className="view-toggle-button"
          onClick={() => setShowSettings(true)}
          title="Settings"
          data-no-sync="true"
        >
          ⚙️
        </button>
        <button
          className={`view-toggle-button ${viewMode === 'map' ? 'active' : ''}`}
          onClick={() => setViewMode(viewMode === 'map' ? 'action' : 'map')}
          data-no-sync="true"
          disabled={(isInCombat || isQuestActive) && viewMode === 'action'}
          style={{
            opacity: ((isInCombat || isQuestActive) && viewMode === 'action') ? 0.5 : 1,
            cursor: ((isInCombat || isQuestActive) && viewMode === 'action') ? 'not-allowed' : 'pointer',
          }}
          title={(isInCombat || isQuestActive) && viewMode === 'action' 
            ? (isQuestActive ? "Cannot view map during quest" : "Cannot view map during combat")
            : viewMode === 'map' ? "View Story" : "View Map"}
        >
          {viewMode === 'map' ? '📖 Story' : '🗺️ Map'}
        </button>
      </div>
      
      {/* Settings Panel */}
      {showSettings && (
        <SettingsPanel 
          onClose={() => setShowSettings(false)}
          islandCenter={islandCenter || map?.startPosition}
          onResetPosition={() => {
            if (map?.startPosition) {
              console.log('🗺️ Resetting player position to island center:', map.startPosition);
              updatePosition(map.startPosition);
              // Also mark nearby hexes as explored
              const neighbors = [
                { q: map.startPosition.q + 1, r: map.startPosition.r },
                { q: map.startPosition.q - 1, r: map.startPosition.r },
                { q: map.startPosition.q, r: map.startPosition.r + 1 },
                { q: map.startPosition.q, r: map.startPosition.r - 1 },
                { q: map.startPosition.q + 1, r: map.startPosition.r - 1 },
                { q: map.startPosition.q - 1, r: map.startPosition.r + 1 },
              ];
              markHexesExplored([map.startPosition, ...neighbors]);
            }
          }}
          onFullHPClick={resetHP}
          onFullStaminaClick={resetStamina}
          onAIToggleClick={toggleAIStories}
          aiStoriesEnabled={aiStoriesEnabled}
          onLeaveGameClick={handleLeaveGame}
          isInCombat={isInCombat}
          onRegenerateMapClick={async () => {
            if (!user) return;
            
            console.log('🗺️ Regenerating map...');
            
            // Generate new map with random seed
            const randomSeed = `storyworld-poc-v1-${Date.now()}`;
            const newMap = generateMap(288, 162, randomSeed);
            setMap(newMap);
            
            // Select new spawn hex
            const newSpawnHex = selectRandomSpawnHex(newMap);
            console.log(`🏠 New spawn hex: (${newSpawnHex.q}, ${newSpawnHex.r})`);
            
            // Update player in Firestore
            const playerRef = doc(db, 'players', user.uid);
            await updateDoc(playerRef, {
              spawnHex: newSpawnHex,
              mapSeed: randomSeed,
              position: newSpawnHex,
              exploredHexes: {}, // Clear explored hexes
            });
            
            // Update local state
            updatePlayer({ 
              spawnHex: newSpawnHex,
              mapSeed: randomSeed,
            });
            updatePosition(newSpawnHex);
            
            // Mark new spawn and neighbors as explored
            const neighbors = hexNeighbors(newSpawnHex);
            markHexesExplored([newSpawnHex, ...neighbors]);
            
            console.log('✅ Map regenerated successfully');
          }}
        />
      )}
      
      {/* Lore Codex */}
      {showLoreCodex && user && (
        <LoreCodex
          userId={user.uid}
          onClose={() => setShowLoreCodex(false)}
        />
      )}
      
      {/* Onboarding Modal */}
      {showOnboarding && (
        <OnboardingModal 
          onClose={handleCloseOnboarding}
          onMarkerSelect={handleMarkerSelect}
        />
      )}

      {/* Conditional Rendering */}
      {viewMode === 'map' && map && (
        <>
          <MapContainer
            map={map}
            initialCenter={initialCenterForMap}
            initialZoom={initialZoom}
            hasUnresolvedMonster={hasUnresolvedMonsterForMap}
            isInCombat={isInCombat}
            onZoomChange={setCurrentZoom}
            onHexSelect={handleHexSelect}
            onPlayerMarkerClick={handlePlayerMarkerClick}
            onDistantHexClick={handleMiniMapHexClick}
            pendingDestination={pendingDestination}
            plannedRoute={plannedRouteForMap}
            onAnimationComplete={handleAnimationComplete}
            isStoryReady={isAnimationReady}
            nextQuestHex={nextQuestHex}
            player={player}
          />
          {/* Story Container (shows travel countdown or story) - hide when in combat */}
          {!isInCombat && (
            <StoryContainer
              storyText={pendingEncounter?.storyText || explorationState.currentStory?.text || null}
              audioUrl={pendingEncounter?.audioUrl || explorationState.currentStory?.audioUrl || null}
              isLoading={explorationState.isLoading}
              hasMonster={!!pendingEncounter || explorationState.currentStory?.hasMonster || false}
              isUnexploredHex={explorationState.currentStory?.isUnexploredHex}
              travelState={travelState}
              destinationTerrain={travelState ? map.hexes.get(`${travelState.destinationHex.q},${travelState.destinationHex.r}`)?.terrain : undefined}
              actionCompletionMessage={actionCompletionMessage}
              // Note: Quest content intentionally only appears in Action view (ActionUI)
              // because players need interactive choices which aren't suitable for the map view
              onTravelComplete={handleTravelComplete}
              onHexArrival={handleHexArrival}
              onTravelCancel={() => {
                console.log('🚫 Cancelling travel');
                cancelTravel();
              }}
              onFightClick={() => {
                if (pendingEncounter || explorationState.currentStory?.hasMonster) {
                  // Clear any action completion message when entering combat
                  setActionCompletionMessage(null);
                  // Force combat UI to show
                  setShowCombatUI(true);
                  // Use the monster that was already selected during story generation
                  // pendingEncounter is already set by useExploration or loaded from Firestore
                  // CombatUI will mount and initialize combat state
                  const monsterName = pendingEncounter?.monsterName || explorationState.currentStory?.monsterName;
                  console.log(`⚔️ Starting combat with: ${monsterName}`);
                  // Don't clear story yet - let it stay visible until combat actually starts
                  // Story will be hidden by isInCombat check above
                  setViewMode('action');
                }
              }}
            />
          )}
        </>
      )}
      
      {viewMode === 'action' && map && (
        <>
          <ActionUI
            player={player}
            monster={monster}
            combatState={combatState || undefined}
            setCombatState={setCombatState}
            storyText={pendingEncounter?.storyText || explorationState.currentStory?.text || null}
            audioUrl={pendingEncounter?.audioUrl || explorationState.currentStory?.audioUrl || null}
            isLoading={explorationState.isLoading}
            onAction={handleAction}
            terrain={currentTerrain}
            showStory={!isInCombat}
            monsterName={combatMonsterName}
            isInCombat={isInCombat}
            showCombatUI={showCombatUI}
            onTerrainImageClick={handleTerrainImageClick}
            hasMonster={!!pendingEncounter || explorationState.currentStory?.hasMonster || false}
            onFightClick={handleFightClick}
            isUnexploredHex={explorationState.currentStory?.isUnexploredHex}
            map={map}
            playerPosition={playerPosition}
            exploredHexes={exploredHexes}
            onHexClick={handleMiniMapHexClick}
            onViewMapClick={handleViewMapClick}
            onQuestStatusChange={setIsQuestActive}
            onNextQuestHexChange={setNextQuestHex}
            questPlaythrough={questPlaythrough}
            questDataLoaded={questDataLoaded}
            updateQuestPlaythrough={updateQuestPlaythrough}
            updatePlayer={updatePlayer}
            updatePosition={updatePosition}
            travelState={travelState}
            destinationTerrain={travelState ? map.hexes.get(`${travelState.destinationHex.q},${travelState.destinationHex.r}`)?.terrain : undefined}
            onTravelComplete={handleTravelComplete}
            onHexArrival={handleHexArrival}
            foragingState={foragingState ? {
              completionTime: foragingState.completionTime,
              terrainType: foragingState.terrainType,
            } : null}
            restingState={restingState ? {
              completionTime: restingState.completionTime,
            } : null}
            onRationClick={handleRationClick}
            onForagingComplete={handleForagingComplete}
            onRestingComplete={handleRestingComplete}
            onForageClick={handleForageClick}
            onRestHealClick={handleRestHealClick}
            actionCompletionMessage={actionCompletionMessage}
            onActionComplete={setActionCompletionMessage}
            pendingDestination={pendingDestination}
            onAnimationComplete={handleAnimationComplete}
            isStoryReady={isAnimationReady}
            plannedRoute={travelState?.route || proposedRoute || undefined}
            combatUI={
              (() => {
                // Determine the monster name from available sources
                const effectiveMonsterName = combatMonsterName || pendingEncounter?.monsterName || explorationState.currentStory?.monsterName || '';
                
                // Only render CombatUI if we have a valid monster name
                if ((isInCombat || pendingEncounter || showCombatUI) && effectiveMonsterName) {
                  return (
                    <CombatUI
                      monsterName={effectiveMonsterName}
                      party={getCombatParty()}
                      partySize={1 as PartySize}
                      onCombatEnd={handleCombatEnd}
                      // No onFlee prop = no flee button (exploration combat is fight-to-the-end)
                    />
                  );
                }
                return undefined;
              })()
            }
          />
        </>
      )}
      
      {/* Travel Modal */}
      {showTravelModal && proposedRoute && modalDestination && map && (
        <TravelModal
          destination={modalDestination}
          destinationTerrain={map.hexes.get(`${modalDestination.q},${modalDestination.r}`)?.terrain || TerrainType.Forest}
          routeDistance={proposedRoute.length}
          staminaCost={calculateStaminaCost(proposedRoute)}
          travelTime={calculateTravelTime(proposedRoute, getTravelTimeSetting())}
          playerStamina={player.stamina}
          onConfirm={handleTravelConfirm}
          onCancel={handleTravelCancel}
        />
      )}
    </div>
  );

  // Main routing structure
  return (
    <Routes>
      {/* Public landing page */}
      <Route path="/" element={<LandingPage />} />
      
      {/* Authentication route */}
      <Route path="/login" element={<AuthScreen />} />
      
      {/* Email verification route */}
      <Route path="/verify" element={
        user && !user.emailVerified ? (
          <EmailVerificationScreen />
        ) : user ? (
          <Navigate to="/worlds" replace />
        ) : (
          <Navigate to="/login" replace />
        )
      } />
      
      {/* World selection fallback route */}
      <Route path="/worlds" element={
        user ? (
          user.emailVerified ? (
            <WorldSelectionScreen
              onSelectWorld={(worldId) => {
                setSelectedWorld(worldId);
                navigate(`/play/${worldId}`);
              }}
            />
          ) : (
            <Navigate to="/verify" replace />
          )
        ) : (
          <Navigate to="/login?redirectTo=/worlds" replace />
        )
      } />
      
      {/* Protected game route */}
      <Route path="/play/:worldId" element={
        <GameRouteWrapper
          renderGame={renderGame}
          user={user}
          isGenerating={isGenerating}
          map={map}
          selectedWorld={selectedWorld}
          setSelectedWorld={setSelectedWorld}
          handleLeaveGame={handleLeaveGame}
        />
      } />
      
      {/* Catch-all redirect to landing page */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default App;
